import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaRulerCombined, FaCalendarAlt, FaMapMarkerAlt, FaTools, FaTags, FaArrowLeft, FaBuilding } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../config/api';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const TrabajoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trabajo, setTrabajo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    fade: true
  };

  useEffect(() => {
    const fetchTrabajo = async () => {
      try {
        const response = await axios.get(`${API_URL}/trabajos/${id}`);
        setTrabajo(response.data);
      } catch (err) {
        console.error('Error fetching trabajo:', err);
        setError('No se pudo cargar el trabajo');
      } finally {
        setLoading(false);
      }
    };

    fetchTrabajo();
  }, [id]);

  const getImageUrl = (imagen) => {
    if (!imagen) return '/stand-jefo.png';
    if (imagen.startsWith('http') || imagen.startsWith('/')) return imagen;
    return `${API_URL}/${imagen}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error || !trabajo) {
    return (
      <Container className="py-5 text-center">
        <h3 className="text-muted">{error || 'Trabajo no encontrado'}</h3>
        <Button variant="primary" onClick={() => navigate('/proyectos')} className="mt-3">
          Volver a la galería
        </Button>
      </Container>
    );
  }

  // Determine images to show
  const imagesToShow = (trabajo.imagenes && trabajo.imagenes.length > 0)
    ? trabajo.imagenes
    : (trabajo.imagen ? [trabajo.imagen] : []);

  return (
    <div id="trabajo-detalle">
      {/* Hero Section */}
      <section
        className="position-relative"
        style={{
          height: '50vh',
          minHeight: '400px',
          marginTop: '-80px',
        }}
      >
        {/* Background Layer: Slider or Static Image */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          {imagesToShow.length > 1 ? (
            <Slider {...sliderSettings} className="h-100 w-100">
              {imagesToShow.map((img, idx) => (
                <div key={idx} className="h-100 w-100">
                  <div
                    style={{
                      width: '100%',
                      height: '50vh', // Ensure height matches container
                      minHeight: '400px',
                      background: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${getImageUrl(img)}) center/cover no-repeat`
                    }}
                  />
                </div>
              ))}
            </Slider>
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${getImageUrl(imagesToShow[0])}) center/cover no-repeat`
              }}
            />
          )}
        </div>

        {/* Content Overlay */}
        <Container className="h-100 d-flex flex-column justify-content-end pb-5 position-relative" style={{ zIndex: 1, pointerEvents: 'none' }}>
          <div style={{ pointerEvents: 'auto' }}>
            <Button
              variant="outline-light"
              onClick={() => navigate('/proyectos')}
              className="mb-4 align-self-start"
              style={{ borderRadius: '25px', borderWidth: '2px', padding: '8px 24px', fontWeight: '600' }}
            >
              <FaArrowLeft className="me-2" />
              Volver a trabajos
            </Button>
            <div className="mb-3">
              <span
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid white',
                  color: 'white',
                  fontSize: '0.9rem',
                  padding: '8px 20px',
                  fontWeight: '500',
                  borderRadius: '25px',
                  display: 'inline-block'
                }}
              >
                {trabajo.categoria}
              </span>
            </div>
            <h1 className="text-white fw-bold display-5">{trabajo.nombre}</h1>
            {trabajo.titulo && (
              <p className="text-white-50 fs-5">{trabajo.titulo}</p>
            )}
          </div>
        </Container>
      </section>

      {/* Contenido */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <Container>
          {/* Descripción y detalles en una fila */}
          <Row className="g-4">
            {/* Descripción */}
            <Col lg={7}>
              {trabajo.descripcion && (
                <div className="mb-4">
                  <h4 style={{ color: '#1E5096' }} className="mb-3">Sobre este proyecto</h4>
                  <p className="fs-5 text-muted" style={{ lineHeight: '1.8' }}>
                    {trabajo.descripcion}
                  </p>
                </div>
              )}

              {/* Galería de imágenes adicionales (si las hay) */}
              {trabajo.imagenes && trabajo.imagenes.length > 0 && (
                <div className="mt-4">
                  <h4 style={{ color: '#1E5096' }} className="mb-3">Galería</h4>
                  <Row className="g-3">
                    {trabajo.imagenes.map((img, index) => (
                      <Col key={index} xs={6} md={4}>
                        <img
                          src={getImageUrl(img)}
                          alt={`${trabajo.nombre} - ${index + 1}`}
                          className="w-100 rounded shadow-sm"
                          style={{ height: '150px', objectFit: 'cover' }}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Col>

            {/* Detalles del proyecto */}
            <Col lg={5}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <h5 style={{ color: '#1E5096' }} className="mb-4 pb-2 border-bottom">Detalles del Proyecto</h5>

                  <Row className="g-3">
                    {/* Cliente */}
                    {trabajo.cliente && (
                      <Col xs={6}>
                        <div className="d-flex align-items-center">
                          <FaBuilding color="#1E5096" size={16} className="me-2" />
                          <div>
                            <small className="text-muted d-block">Cliente</small>
                            <strong style={{ fontSize: '0.95rem' }}>{trabajo.cliente}</strong>
                          </div>
                        </div>
                      </Col>
                    )}

                    {/* Ubicación */}
                    {trabajo.ubicacion && (
                      <Col xs={6}>
                        <div className="d-flex align-items-center">
                          <FaMapMarkerAlt color="#1E5096" size={16} className="me-2" />
                          <div>
                            <small className="text-muted d-block">Ubicación</small>
                            <strong style={{ fontSize: '0.95rem' }}>{trabajo.ubicacion}</strong>
                          </div>
                        </div>
                      </Col>
                    )}

                    {/* Tamaño */}
                    {trabajo.tamaño && (
                      <Col xs={6}>
                        <div className="d-flex align-items-center">
                          <FaRulerCombined color="#1E5096" size={16} className="me-2" />
                          <div>
                            <small className="text-muted d-block">Tamaño</small>
                            <strong style={{ fontSize: '0.95rem' }}>{trabajo.tamaño}</strong>
                          </div>
                        </div>
                      </Col>
                    )}

                    {/* Fecha */}
                    {trabajo.fecha && (
                      <Col xs={6}>
                        <div className="d-flex align-items-center">
                          <FaCalendarAlt color="#1E5096" size={16} className="me-2" />
                          <div>
                            <small className="text-muted d-block">Año</small>
                            <strong style={{ fontSize: '0.95rem' }}>{new Date(trabajo.fecha).getFullYear()}</strong>
                          </div>
                        </div>
                      </Col>
                    )}

                    {/* Materiales */}
                    {trabajo.materiales && (
                      <Col xs={12}>
                        <div className="d-flex align-items-start mt-2">
                          <FaTools color="#1E5096" size={16} className="me-2 mt-1" />
                          <div>
                            <small className="text-muted d-block">Materiales</small>
                            <strong style={{ fontSize: '0.95rem' }}>{trabajo.materiales}</strong>
                          </div>
                        </div>
                      </Col>
                    )}

                    {/* Categoría */}
                    <Col xs={12}>
                      <div className="d-flex align-items-center mt-2">
                        <FaTags color="#1E5096" size={16} className="me-2" />
                        <div>
                          <small className="text-muted d-block">Categoría</small>
                          <span
                            style={{
                              border: '2px solid #1E5096',
                              color: '#1E5096',
                              fontSize: '0.85rem',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontWeight: '500'
                            }}
                          >
                            {trabajo.categoria}
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* CTA */}
              <Card className="border-0 shadow-sm mt-4" style={{ backgroundColor: '#1E5096' }}>
                <Card.Body className="p-4 text-center text-white">
                  <h5 className="mb-3">¿Te interesa un proyecto similar?</h5>
                  <Button
                    variant="outline-light"
                    onClick={() => navigate('/contacto')}
                    style={{ borderRadius: '25px', borderWidth: '2px', padding: '8px 24px', fontWeight: '600' }}
                  >
                    Solicitar Presupuesto
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default TrabajoDetalle;
